import { useState } from "react";

import { createSupabaseBrowserClient } from "@/lib/supabase/client";

type Props = {
  email: string;
  initial: {
    display_name: string;
    phone: string;
    company_name: string;
    notes: string;
  };
};

export function ProfileForm({ email, initial }: Props) {
  const [displayName, setDisplayName] = useState(initial.display_name);
  const [phone, setPhone] = useState(initial.phone);
  const [companyName, setCompanyName] = useState(initial.company_name);
  const [notes, setNotes] = useState(initial.notes);

  const [profileMsg, setProfileMsg] = useState<{ ok?: boolean; text: string } | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);

  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passMsg, setPassMsg] = useState<{ ok?: boolean; text: string } | null>(null);
  const [passSaving, setPassSaving] = useState(false);

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const sb = createSupabaseBrowserClient();
      const {
        data: { user },
      } = await sb.auth.getUser();
      if (!user?.id) {
        setProfileMsg({ ok: false, text: "Not signed in." });
        return;
      }
      const { error } = await sb.from("profiles").upsert(
        {
          id: user.id,
          display_name: displayName.trim() || null,
          phone: phone.trim() || null,
          company_name: companyName.trim() || null,
          notes: notes.trim() || null,
        },
        { onConflict: "id" },
      );
      if (error) {
        setProfileMsg({ ok: false, text: error.message });
        return;
      }
      setProfileMsg({ ok: true, text: "Saved." });
    } catch {
      setProfileMsg({ ok: false, text: "Network error." });
    } finally {
      setProfileSaving(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPassMsg(null);
    if (newPass.length < 8) {
      setPassMsg({ ok: false, text: "Use at least 8 characters." });
      return;
    }
    if (newPass !== confirmPass) {
      setPassMsg({ ok: false, text: "New password and confirmation must match." });
      return;
    }
    setPassSaving(true);
    try {
      const sb = createSupabaseBrowserClient();
      const { error } = await sb.auth.updateUser({ password: newPass });
      if (error) {
        setPassMsg({ ok: false, text: error.message });
        return;
      }
      setNewPass("");
      setConfirmPass("");
      setPassMsg({ ok: true, text: "Password updated." });
    } catch {
      setPassMsg({ ok: false, text: "Could not update password." });
    } finally {
      setPassSaving(false);
    }
  }

  return (
    <div className="mt-8 space-y-10">
      <form onSubmit={(e) => void saveProfile(e)} className="space-y-5 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Contact & details</h2>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-zinc-800">Email</label>
          <input
            readOnly
            value={email}
            className="w-full cursor-not-allowed rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-600"
          />
          <p className="text-xs text-zinc-500">Tied to your account email. To change it, contact support.</p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="display_name" className="text-sm font-semibold text-zinc-800">
            Name / how we should address you
          </label>
          <input
            id="display_name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none ring-violet-400 focus:ring-2"
            placeholder="Optional"
            autoComplete="name"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="phone" className="text-sm font-semibold text-zinc-800">
            Phone
          </label>
          <input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none ring-violet-400 focus:ring-2"
            placeholder="Optional"
            autoComplete="tel"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="company" className="text-sm font-semibold text-zinc-800">
            Store / brand / company
          </label>
          <input
            id="company"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none ring-violet-400 focus:ring-2"
            placeholder="Optional"
            autoComplete="organization"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="notes" className="text-sm font-semibold text-zinc-800">
            Anything else we should know
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full resize-y rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none ring-violet-400 focus:ring-2"
            placeholder="Optional — niche, timezone, link to your shop…"
          />
        </div>

        {profileMsg ? (
          <p className={`text-sm font-medium ${profileMsg.ok ? "text-emerald-700" : "text-rose-600"}`}>{profileMsg.text}</p>
        ) : null}

        <button
          type="submit"
          disabled={profileSaving}
          className="min-h-11 rounded-xl bg-violet-600 px-5 text-sm font-bold text-white shadow-md transition hover:bg-violet-700 disabled:opacity-60"
        >
          {profileSaving ? "Saving…" : "Save profile"}
        </button>
      </form>

      <form onSubmit={(e) => void changePassword(e)} className="space-y-5 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Password</h2>
        <p className="text-xs text-zinc-600">Update the password you use to sign in.</p>

        <div className="space-y-1.5">
          <label htmlFor="new_pass" className="text-sm font-semibold text-zinc-800">
            New password
          </label>
          <input
            id="new_pass"
            type="password"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none ring-violet-400 focus:ring-2"
            autoComplete="new-password"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="confirm_pass" className="text-sm font-semibold text-zinc-800">
            Confirm new password
          </label>
          <input
            id="confirm_pass"
            type="password"
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none ring-violet-400 focus:ring-2"
            autoComplete="new-password"
          />
        </div>

        {passMsg ? (
          <p className={`text-sm font-medium ${passMsg.ok ? "text-emerald-700" : "text-rose-600"}`}>{passMsg.text}</p>
        ) : null}

        <button
          type="submit"
          disabled={passSaving}
          className="min-h-11 rounded-xl border border-zinc-300 bg-white px-5 text-sm font-bold text-zinc-900 shadow-sm transition hover:bg-zinc-50 disabled:opacity-60"
        >
          {passSaving ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
